import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { IonContent, ModalController, PopoverController } from '@ionic/angular';
import { combineLatest, firstValueFrom, Observable, Subscription, switchMap } from 'rxjs';

import {
  ChatGroup,
  ChatMessage,
  DefaultChatGroups,
  RoleLimits,
  ParticipantFsDolby,
  PromptTypes,
  ParticipantFsDaily,
} from '@sc/types';
import { ChatService } from '../../services/chat/chat.service';
import { UserService } from '../../services/user/user.service';
import { RolesService } from '../../services/roles/roles.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { InviteChatGroupMenuComponent } from '../../menus/invite-chat-group-menu/invite-chat-group-menu.component';
import { NameService } from '../../services/name/name.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { DolbyService } from '../../services/dolby/dolby.service';
import { ActionConfirmPage } from '../../modals/action-confirm/action-confirm.page';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { LafayetteService } from '../../services/lafayette/lafayette.service';
import { escapeHTML } from '../../util/escapeHTML';
import { SCSubject } from '../../util/sc-subject.class';
import { DailyService } from '../../services/daily/daily.service';

@Component({
  selector: 'sc-chat-group',
  templateUrl: './chat-group.component.html',
  styleUrls: ['./chat-group.component.scss'],
})
export class ChatGroupComponent implements OnInit, OnDestroy, OnChanges {
  @Output() closeSidebar = new EventEmitter();
  @Input() chatGroupID: string;
  @Input() goBack: () => void;
  @ViewChild(IonContent) content: IonContent;

  studioSession$ = this.sessionsService.studioSession$;
  dolbyParticipants$ = this.dolbyService.getStageAndBackstageParticipants();
  dolbyConversation$ = this.dolbyService.dolbyConversation$;
  dailyParticipants$ = this.dailyService.stageParticipants$;
  dailyCall$ = this.dailyService.dailyCall$;
  chatGroup$: SCSubject<ChatGroup> = new SCSubject<ChatGroup>(null);
  activeUser$ = this.userService.activeUser$;
  dolbyParticipants = new Map<string, ParticipantFsDolby>();
  dailyParticipants = new Map<string, ParticipantFsDaily>();
  chatMessages$: Observable<Array<ChatMessage>>;
  chatMessages: Array<ChatMessage>;
  newMessage: string;
  mentionConfig: any;
  name: string;
  defaultChatGroups = DefaultChatGroups;

  role$ = this.rolesService.role$;
  roleLimits = RoleLimits;

  gifMenuOpen = false;
  gifSearchQuery: string;

  scrolling = false;
  scrollingDisabled = false;
  unreadCount = 0;

  subs: Array<Subscription> = [];

  constructor(
    public rolesService: RolesService,
    private popoverController: PopoverController,
    private sessionsService: SessionsService,
    private chatService: ChatService,
    private userService: UserService,
    private nameService: NameService,
    private analyticsService: AnalyticsService,
    private dolbyService: DolbyService,
    private dailyService: DailyService,
    private modalController: ModalController,
    private lafayetteService: LafayetteService
  ) {}

  /**
   * Initializes the chat group component
   */
  ngOnInit() {
    this.setupChatParticipants();
    this.setupName();
    this.setupChatMessages();
    this.setupUnreadChatGroups();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.chatGroupID) {
      this.chatGroup$.next(
        await firstValueFrom(this.chatService.getChatGroupByID(this.studioSession$.value.sessionID, this.chatGroupID))
      );
    }
  }

  async setupChatMessages() {
    await this.studioSession$.nextExistingValue();
    await this.activeUser$.nextExistingValue();

    const sub = this.chatGroup$
      .pipe(
        switchMap((chatGroup) =>
          this.chatService.getChatMessages(this.studioSession$.value.sessionID, chatGroup.chatGroupID)
        )
      )
      .subscribe((messages) => {
        this.unreadCount++;
        this.chatMessages = messages.reverse();
        this.scrollToBottom();
      });
    this.subs.push(sub);
  }

  setupUnreadChatGroups() {
    const sub = combineLatest([this.chatService.unreadChatGroups$, this.chatGroup$]).subscribe(
      ([unreadChatGroups, chatGroup]) => {
        this.chatService.unreadChatGroups$.next(unreadChatGroups.filter((id) => id !== chatGroup.chatGroupID));
      }
    );
    this.subs.push(sub);
  }

  /**
   * Sends a new chat message
   */
  async sendChatMessage() {
    if (!this.newMessage) {
      return;
    }
    let message = this.newMessage;
    this.newMessage = '';
    message = escapeHTML(message);

    try {
      // slash commands
      if (message.charAt(0) === '/') {
        const results = await this.chatService.handleChatCommand(
          {
            message,
            datetime: Date.now().toString(),
            role: this.rolesService.role$.value,
            name: this.name,
            uid: this.activeUser$.value.uid,
          },
          this.chatGroupID
        );

        this.processCommandResult(results);

        return;
      }

      // @mentions
      const atMentionRegex = /@.+/g;
      const atMention = message.match(atMentionRegex);
      if (atMention) {
        const atNames = atMention.toString().substring(1);
        let atParticipants;

        if (this.dolbyConversation$.value) {
          const dolbyParticipants = await firstValueFrom(this.dolbyParticipants$);
          atParticipants = dolbyParticipants.filter(
            (participant) => participant.name && atNames.includes(participant.name)
          );
        }

        if (this.dailyCall$.value) {
          const dailyParticipants = await firstValueFrom(this.dailyService.stageParticipants$);
          console.log('[CHAT]', Array.from(dailyParticipants));
          const dailyAtParticipants = [];
          dailyParticipants.forEach((participant) => {
            if (participant.name && atNames.includes(participant.name)) {
              dailyAtParticipants.push(participant);
            }
          });

          atParticipants = dailyAtParticipants;
        }

        const chatGroup = await firstValueFrom(
          this.chatService.getChatGroupByID(this.studioSession$.value.sessionID, this.chatGroupID)
        );

        const atMentionMessageRaw = message;

        atParticipants.forEach((atParticipant) => {
          const atEndIndex = message.indexOf(atParticipant.name) + atParticipant.name.length;
          // Put super rare '@*^$@%' at the end of the message to let the pipe know that it ends here.
          message = message.substring(0, atEndIndex) + '@*^$@%' + message.substring(atEndIndex);
        });

        atParticipants.forEach(async (atParticipant) => {
          // add participant to chat group if they're not already in it
          if (!chatGroup.users.some((uid) => uid === atParticipant.uid || uid === atParticipant.userData.scUID)) {
            this.chatService.addParticipantToChatGroup(
              this.studioSession$.value.sessionID,
              this.chatGroupID,
              atParticipant.uid || atParticipant.userData.scUID
            );
          }

          // prompt the mentioned participant
          await this.sessionsService.createSessionPrompt(
            {
              title: `${this.name} mentioned you in a chat message`,
              message: atMentionMessageRaw,
              creator: this.activeUser$.value.uid,
              presentAs: `toast`,
              type: PromptTypes.ATMENTION,
              targetParticipants: [atParticipant.uid],
              data: { chatGroupID: this.chatGroupID },
            },
            this.studioSession$.value.sessionID
          );
        });
      }

      this.chatService.createChatMessage(this.studioSession$.value.sessionID, this.chatGroupID, {
        message,
        datetime: Date.now().toString(),
        role: this.rolesService.role$.value,
        name: this.name,
        uid: this.activeUser$.value.uid,
      });
      // prompt the participants in chat group
      this.sessionsService.createSessionPrompt(
        {
          title: `${this.name} sent a chat message`,
          message,
          creator: this.activeUser$.value.uid,
          presentAs: `toast`,
          type: PromptTypes.UNREADCHATGROUP,
          targetChatGroupID: this.chatGroupID,
          data: { chatGroupID: this.chatGroupID },
        },
        this.studioSession$.value.sessionID
      );

      this.scrollingDisabled = false;
      this.scrollToBottom();

      await this.analyticsService.track('sent chat message', { success: true });
    } catch (error) {
      await this.analyticsService.track('sent chat message', { success: false });
    }
  }

  /**
   * Opens the invite chat group menu
   *
   * @param click
   */
  async openInviteChatGroupMenu(click: MouseEvent) {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: InviteChatGroupMenuComponent,
      event: click,
      componentProps: {
        chatGroupID: this.chatGroupID,
      },
    });
    await menu.present();
    await this.analyticsService.track('opened invite to chat group menu');
  }

  /**
   * Deletes this Chat Group
   * Navigates your back to Chat Groups
   */
  async deleteChatGroup() {
    const modal = await this.modalController.create({
      component: ActionConfirmPage,
      componentProps: {
        title: 'Delete Chat Group',
        message: `Are you sure you want to delete ${this.chatGroup$.value.name}?`,
        icon: 'delete',
        buttons: [
          {
            label: 'Delete Chat Group',
            handler: async () => {
              await this.lafayetteService.deleteChatGroup(this.studioSession$.value.sessionID, this.chatGroupID);
              await this.goBack();
              await this.analyticsService.track('deleted chat group', { chatGroupName: this.chatGroup$.value.name });
              modal.dismiss();
            },
            type: 'danger',
          },
          {
            label: 'Cancel',
            handler: () => {
              modal.dismiss();
              this.analyticsService.track('closed delete chat group', { chatGroupName: this.chatGroup$.value.name });
            },
          },
        ],
      },
      showBackdrop: true,
      backdropDismiss: true,
      animated: true,
      cssClass: 'action-confirm-modal',
    });

    modal.present();
    this.analyticsService.track('selected delete chat group', { chatGroupName: this.chatGroup$.value.name });
  }

  /**
   * Inserts the selected giphy url
   * Closes the gif menu
   * Sends the chat message
   * Resets the new message
   *
   * @param gif
   */
  async insertGif(gif: any) {
    this.newMessage = gif.images.fixed_height.url;
    this.toggleGifMenu();
    await this.sendChatMessage();
    this.newMessage = undefined;
    await this.analyticsService.track('sent chat gif', { gif: this.newMessage });
  }

  /**
   * Toggles the gif menu
   */
  async toggleGifMenu() {
    this.gifSearchQuery = this.newMessage;
    this.gifMenuOpen = !this.gifMenuOpen;

    await this.analyticsService.track('toggled chat gif', { expanded: this.gifMenuOpen });
  }

  /**
   * Scrolls the list of chat messages to the bottom
   */
  async scrollToBottom(animationTime = 200) {
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (this.scrolling || this.scrollingDisabled) return;
    this.scrolling = true;
    const scrollElm = await this.content.getScrollElement();

    if (scrollElm.scrollTop <= scrollElm.scrollHeight - scrollElm.clientHeight - 5) {
      this.content.scrollToBottom(animationTime);
      await new Promise((resolve) => setTimeout(resolve, animationTime + 50));
      this.scrolling = false;
      this.scrollToBottom(animationTime);
    }
    this.scrolling = false;
  }

  async onScroll(e) {
    if (e.detail.deltaY <= -50 && !this.scrollingDisabled) {
      this.scrollingDisabled = true;
      this.unreadCount = 0;
      return;
    }

    const scrollElm = await this.content.getScrollElement();
    if (scrollElm.scrollTop + scrollElm.clientHeight + 5 > scrollElm.scrollHeight) {
      this.scrollingDisabled = false;
    }
  }

  /**
   * Sets up the mention configuration to include all participants
   */
  setupChatParticipants() {
    if (this.dolbyConversation$.value) {
      const dolbySub = this.dolbyParticipants$.subscribe((participants) => {
        participants.forEach((p) => this.dolbyParticipants.set(p.uid, p));

        console.log('[CHAT DOLBY] atMentionParticipants', participants);

        this.mentionConfig = {
          mentions: [
            {
              triggerChar: '@',
              items: participants || [],
              dropUp: true,
              labelKey: 'name',
              allowSpace: true,
              mentionSelect: (item) => '@' + item.name + ' ',
            },
            // {
            //   triggerChar: '#',
            //   items: ['offTheRecord', 'addToIntro'],
            //   dropUp: true,
            // },
            {
              triggerChar: '/',
              items: ['help', 'gif', 'dark', 'light', 'roll'],
              dropUp: true,
              allowSpace: true,
              mentionSelect: (item) => '/' + item.label + ' ',
            },
          ],
        };
      });
      this.subs.push(dolbySub);
    }

    if (this.dailyCall$.value) {
      const dailySub = this.dailyService.stageParticipants$.subscribe((participants) => {
        const atMentionParticipants = [];

        participants.forEach(async (participant: ParticipantFsDaily & { name$: SCSubject<string> }, key) => {
          if (participant.name$) {
            participant.name = await firstValueFrom(participant.name$);
            this.dailyParticipants.set(key, participant);
            atMentionParticipants.push(participant);
          }
        });

        this.mentionConfig = {
          mentions: [
            {
              triggerChar: '@',
              items: atMentionParticipants,
              dropUp: true,
              labelKey: 'name',
              allowSpace: true,
              mentionSelect: (item) => '@' + item.name + ' ',
            },
            // {
            //   triggerChar: '#',
            //   items: ['offTheRecord', 'addToIntro'],
            //   dropUp: true,
            // },
            {
              triggerChar: '/',
              items: ['help', 'gif', 'dark', 'light', 'roll'],
              dropUp: true,
              allowSpace: true,
              mentionSelect: (item) => '/' + item.label + ' ',
            },
          ],
        };

        console.log('[CHAT]', this.mentionConfig);
      });
      this.subs.push(dailySub);
    }
  }

  processCommandResult(results: { chatCommand: string; message?: string; error?: string; chatMessage?: ChatMessage }) {
    this.gifMenuOpen = false;
    this.gifSearchQuery = this.newMessage;

    if (results.chatCommand === 'gif') {
      this.gifSearchQuery = results.chatMessage.message.split(' ').slice(1).join(' ');
      this.gifMenuOpen = true;
    }
  }

  /**
   * Sets up the name of the local participant
   */
  setupName() {
    const sub = this.nameService
      .getName(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
      .subscribe((name) => {
        this.name = name;
      });

    this.subs.push(sub);
  }

  /**
   * Prevents flickering/flashing in the list of chat messages
   *
   * @param index - number
   * @param chatMessage - ChatMessage
   * @returns - string
   */
  trackByChatMessage(index: number, chatMessage: ChatMessage) {
    return chatMessage.chatMessageID;
  }

  ngOnDestroy() {
    this.mentionConfig = undefined;

    this.subs.map((sub) => {
      sub.unsubscribe();
    });
  }
}
